import {
  EngineModelFilterNames,
  ModelFilterNames,
  ModelFilterTechnicalDataGroup,
  SingleTechnicalDataGroup,
} from '@volkswagen-onehub/car-technical-details-bff';

export function getFilterEntriesOfVariation(
  variation: SingleTechnicalDataGroup,
  modelFilterName: string
): string[] {
  return (variation.additionalFilters[modelFilterName] || [])
    .concat(variation.modelFilters[modelFilterName] || [])
    .map((filter) => filter.text);
}

/**
 * returns data field to filter for and the corresponding entries.
 * {
 *   modelFilterName: EquipmentLine,
 *   filterEntries: [
 *     [Life, Style],
 *     [Move, Performance],
 *     [GTI],
 *   ]
 * }
 * In this example the filtering will be done for the Equipment Line. Some are
 * grouped since the car settings will be the same for all of this group. So
 * there is one entry for Life and Style, one entry for Move and Performance
 * and a third for GTI.
 * @param group
 */
export function getFilter(group: ModelFilterTechnicalDataGroup) {
  if (group.variations.length <= 1) {
    return undefined;
  }

  if (group.wltpGroup) {
    return getFilterByType(ModelFilterNames.FuelType, group);
  }

  // check if engine is dominant in model filter.
  const isEngineType =
    EngineModelFilterNames.find((modelFilterName) =>
      group.discriminatingModelFilterTypes.includes(modelFilterName)
    ) &&
    !group.discriminatingModelFilterTypes.includes(
      ModelFilterNames.EquipmentLine
    );

  const modelFilterName = isEngineType
    ? ModelFilterNames.FuelType
    : ModelFilterNames.EquipmentLine;
  const filter = getFilterByType(modelFilterName, group);

  if (isEngineType && (!filter || filter?.filterEntryList.length == 1)) {
    // if fuel type has only one or no option, then this might be an EV. Check if the trim filter is applicable.
    return getFilterByType(ModelFilterNames.EquipmentLine, group);
  }
  return filter;
}

function getFilterByType(
  modelFilterName: string,
  group: ModelFilterTechnicalDataGroup
) {
  const isSingleFilter =
    group.discriminatingModelFilterTypes.includes(modelFilterName) &&
    group.discriminatingModelFilterTypes.length === 1;

  let filterEntryList: string[][];
  if (isSingleFilter) {
    // entries for filter field can be grouped if possible, if data depends on only on this one field
    const filterSet = group.variations.reduce((acc, cur) => {
      const filterIds = getFilterEntriesOfVariation(cur, modelFilterName);
      acc.add(filterIds.sort());
      return acc;
    }, new Set<string[]>());
    filterEntryList = Array.from(filterSet);
  } else {
    // need one entry for each value of filter field, if data depends on multiple fields
    const filterSet = group.variations.reduce((acc, cur) => {
      const filterIds = getFilterEntriesOfVariation(cur, modelFilterName);
      acc.set(filterIds.join('-'), filterIds);
      return acc;
    }, new Map<string, string[]>());

    filterEntryList = Array.from(filterSet.values());
    // make sure filters don't overlap. Otherwise different filter values select the same variations that makes no sense.
    const singleFilters = filterEntryList.reduce((acc, filterEntry) => {
      filterEntry.forEach((f) => acc.push(f));
      return acc;
    }, [] as string[]);
    if (singleFilters.length > new Set(singleFilters).size) {
      // we have overlapping filters. Don't show them.
      return undefined;
    }
  }

  filterEntryList.sort((a, b) => {
    const compare = b.length - a.length;
    if (compare !== 0) {
      return compare;
    }
    return a[0].localeCompare(b[0]);
  });

  return {
    modelFilterName,
    filterEntryList,
  };
}
